import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Copy, Check, KeyRound, Trash2, RefreshCw, Zap, Loader2, Send, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCampaignIntakeKeys } from '@/hooks/useCampaignIntakeKeys';
import { useToast } from '@/hooks/use-toast';
import { UnifiedCampaign } from '@/types/unifiedCampaign';
import { LEAD_INTAKE_URL } from '@/lib/leadIntake';
import { supabase } from '@/integrations/supabase/client';

interface LiveCampaignSettingsProps {
  campaign: UnifiedCampaign;
  currentUserId?: string;
  /** Persists campaign updates immediately (does NOT close the dialog). */
  onPersist: (updates: Partial<UnifiedCampaign>) => Promise<void>;
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'call_initiated' || status === 'accepted') return 'default';
  if (status.startsWith('rejected') || status === 'call_failed') return 'destructive';
  if (status === 'test_skipped') return 'outline';
  return 'secondary';
}

export function LiveCampaignSettings({
  campaign,
  currentUserId,
  onPersist,
}: LiveCampaignSettingsProps) {
  const { keys, events, createKey, revokeKey, revokeAllKeys, refresh } =
    useCampaignIntakeKeys(campaign.id);
  const { toast } = useToast();

  // These reflect persisted campaign state (updated immediately, not on dialog Save).
  const isLive = !!campaign.is_live;
  const callImmediately = campaign.live_call_immediately !== false;
  const consentAttested = !!campaign.live_consent_attested_at;

  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmUnconsent, setConfirmUnconsent] = useState(false);

  // Send Test Lead
  const [testPhone, setTestPhone] = useState('');
  const [realTestCall, setRealTestCall] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const copy = (text: string, what: string) => {
    navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };

  // Toggle live intake on/off. Turning off does not touch keys/consent.
  const handleLiveToggle = async (v: boolean) => {
    setBusy(true);
    try {
      await onPersist({ is_live: v });
    } finally {
      setBusy(false);
    }
  };

  const handleCallImmediately = async (v: boolean) => {
    setBusy(true);
    try {
      await onPersist({ live_call_immediately: v });
    } finally {
      setBusy(false);
    }
  };

  // Checking consent persists the attestation immediately.
  const handleConsentChange = async (checked: boolean) => {
    if (checked) {
      setBusy(true);
      try {
        await onPersist({
          live_consent_attested_by: currentUserId,
          live_consent_attested_at: new Date().toISOString(),
        });
        toast({ title: 'Consent saved', description: 'Attestation recorded automatically.' });
      } finally {
        setBusy(false);
      }
    } else {
      // Withdrawing consent is destructive — confirm first.
      setConfirmUnconsent(true);
    }
  };

  // Confirmed withdrawal: revoke all keys, turn off live, clear attestation.
  const confirmWithdrawConsent = async () => {
    setConfirmUnconsent(false);
    setBusy(true);
    try {
      await revokeAllKeys();
      await onPersist({
        is_live: false,
        live_consent_attested_by: null as unknown as string,
        live_consent_attested_at: null as unknown as string,
      });
      setNewKey(null);
      toast({ title: 'Consent withdrawn', description: 'Live intake disabled and all keys revoked.' });
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async () => {
    if (!consentAttested) {
      toast({
        title: 'Confirm consent first',
        description: 'Check the consent box before generating a key.',
        variant: 'destructive',
      });
      return;
    }
    setGenerating(true);
    try {
      const raw = await createKey('Webhook key');
      if (raw) {
        setNewKey(raw);
        toast({ title: 'Key generated', description: 'Copy it now — it won’t be shown again.' });
      }
    } finally {
      setGenerating(false);
    }
  };

  // Sends a test lead through the real webhook authenticated by the user's
  // session (no API key needed — the webhook verifies the logged-in owner for
  // test requests). Mirrors what Zapier/Meta will do, minus the key.
  const handleSendTest = async () => {
    if (!testPhone.trim()) {
      toast({ title: 'Enter a phone number to test', variant: 'destructive' });
      return;
    }
    setSendingTest(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast({ title: 'Not signed in', description: 'Please sign in again.', variant: 'destructive' });
        setSendingTest(false);
        return;
      }
      const res = await fetch(LEAD_INTAKE_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaign.id,
          phone: testPhone.trim(),
          name: 'Test Lead',
          source: 'test',
          consent: true,
          consent_text: 'In-app test lead',
          test: true,
          testOnly: !realTestCall,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.ok) {
        toast({
          title: realTestCall ? 'Test call placed' : 'Test succeeded',
          description: realTestCall
            ? 'Check the number — a call should arrive shortly. (Test leads are not saved to the list.)'
            : 'Pipeline verified. No call placed and nothing added to the list. See Recent Leads.',
        });
        refresh();
      } else {
        toast({
          title: 'Test failed',
          description: data?.error || `HTTP ${res.status}`,
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'Test failed',
        description: e instanceof Error ? e.message : 'Network error',
        variant: 'destructive',
      });
    } finally {
      setSendingTest(false);
    }
  };

  const activeKeys = keys.filter((k) => k.is_active);

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-500" />
          <Label className="text-base font-semibold">Live Lead Intake</Label>
        </div>
        <Switch checked={isLive} onCheckedChange={handleLiveToggle} disabled={busy} />
      </div>
      <p className="text-xs text-muted-foreground">
        When live, new leads pushed to this campaign&rsquo;s webhook (from Meta, Zapier, Make, a web form,
        your CRM, etc.) are added to the prospect list and called automatically.
      </p>
      <Link
        to="/api-settings?tab=automation"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        <BookOpen className="h-3.5 w-3.5" />
        Full setup guide
      </Link>

      {isLive && (
        <>
          <Separator />

          {/* TCPA attestation — persists immediately */}
          <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3">
            <Checkbox
              id="consent-attest"
              checked={consentAttested}
              onCheckedChange={(v) => handleConsentChange(v === true)}
              disabled={busy}
              className="mt-0.5"
            />
            <Label htmlFor="consent-attest" className="text-xs font-normal leading-snug">
              I confirm my lead forms collect TCPA-compliant prior express written consent for
              AI/automated calls. Each incoming lead must include a consent flag.
              <span className="mt-1 block text-[11px] italic text-muted-foreground">
                Saved automatically when checked.
              </span>
            </Label>
          </div>

          {/* Call immediately */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Call immediately</Label>
              <p className="text-xs text-muted-foreground">
                Off = leads are added to the list but not called automatically.
              </p>
            </div>
            <Switch checked={callImmediately} onCheckedChange={handleCallImmediately} disabled={busy} />
          </div>

          <Separator />

          {/* Webhook URL */}
          <div className="space-y-1.5">
            <Label className="text-sm">Webhook URL</Label>
            <div className="flex gap-2">
              <Input readOnly value={LEAD_INTAKE_URL} className="min-w-0 flex-1 font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => copy(LEAD_INTAKE_URL, 'url')}
              >
                {copied === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              POST leads here with your API key in the <code>Authorization: Bearer</code> header.
            </p>
          </div>

          {/* API keys */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">API Keys</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleGenerate}
                disabled={generating || !consentAttested}
              >
                {generating ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <KeyRound className="mr-2 h-3.5 w-3.5" />
                )}
                {generating ? 'Generating...' : 'Generate Key'}
              </Button>
            </div>

            {/* Freshly created key — shown ONCE */}
            {newKey && (
              <div className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Copy this key now — it won&rsquo;t be shown again.
                </p>
                <div className="flex gap-2">
                  <Input readOnly value={newKey} className="min-w-0 flex-1 font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copy(newKey, 'newkey')}
                  >
                    {copied === 'newkey' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {activeKeys.length === 0 && !newKey && (
              <p className="text-xs text-muted-foreground">
                No keys yet. Generate one to start receiving leads.
              </p>
            )}

            {activeKeys.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="min-w-0 flex-1 text-xs">
                  <span className="font-mono">{k.key_prefix}…</span>
                  {k.label && <span className="ml-2 truncate text-muted-foreground">{k.label}</span>}
                  <div className="text-muted-foreground">
                    {k.request_count} request{k.request_count === 1 ? '' : 's'}
                    {k.last_used_at
                      ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}`
                      : ' · never used'}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => revokeKey(k.id)}
                  title="Revoke key"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <Separator />

          {/* Send Test Lead */}
          <div className="space-y-2 rounded-md bg-muted/40 p-3">
            <Label className="text-sm">Send a Test Lead</Label>
            <p className="text-xs text-muted-foreground">
              Verify your setup before connecting Zapier, Make, a web form, or any other source. Enter a
              phone number and send — no API key needed here (this runs as you).
            </p>
            <div className="flex gap-2">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+15551234567"
                className="min-w-0 flex-1 text-xs"
              />
              <Button
                type="button"
                size="sm"
                className="shrink-0"
                onClick={handleSendTest}
                disabled={sendingTest}
              >
                {sendingTest ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3.5 w-3.5" />
                )}
                Send Test
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Place a real test call</Label>
                <p className="text-[11px] text-muted-foreground">
                  Off = add to list only (no call, no charge). On = actually call the number to hear the
                  campaign.
                </p>
              </div>
              <Switch checked={realTestCall} onCheckedChange={setRealTestCall} />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Test leads are never added to the campaign list. A real test call is logged but flagged as a
              test so it won&rsquo;t count toward campaign stats.
            </p>
          </div>

          <Separator />

          {/* Recent leads */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Recent Leads</Label>
              <Button type="button" variant="ghost" size="icon" onClick={refresh} title="Refresh">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
            {events.length === 0 ? (
              <p className="text-xs text-muted-foreground">No leads received yet.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs px-2">When</TableHead>
                      <TableHead className="text-xs px-2">Phone</TableHead>
                      <TableHead className="text-xs px-2">Source</TableHead>
                      <TableHead className="text-xs px-2">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="text-xs px-2 whitespace-nowrap">
                          {new Date(e.received_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                          })}{' '}
                          {new Date(e.received_at).toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell className="text-xs px-2 font-mono">
                          {e.normalized_phone || '—'}
                        </TableCell>
                        <TableCell className="text-xs px-2">{e.source || 'webhook'}</TableCell>
                        <TableCell className="text-xs px-2">
                          <Badge variant={statusBadgeVariant(e.status)} className="text-[10px]">
                            {e.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}

      <AlertDialog open={confirmUnconsent} onOpenChange={setConfirmUnconsent}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw consent?</AlertDialogTitle>
            <AlertDialogDescription>
              This turns off live intake and immediately revokes all API keys for this campaign.
              Any connected lead sources (Meta, Zapier, forms) will stop working until you re-enable
              consent and generate a new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmWithdrawConsent}>
              Withdraw &amp; revoke keys
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
